import React from 'react';
import appList from 'constants/appList';
import { check as checkBase } from '@nextiva/global-store';
import PropTypes from 'prop-types';
import { get, pick, identity } from 'lodash';
import cx from 'classnames';

import { getInitials, getWidgets } from 'utils';

import { appIcons } from '../../constants';
import HeaderLogo from '../HeaderLogo';
import HeaderButtons from '../HeaderButtons';
import { userDropdownOptions } from '../../constants/userDropdownOptions';
import routes from 'constants/routes';

import '../../styles/application/main.scss';

const WIDGETS = getWidgets();

const blockName = 'nx-global-header';

export const SimpleHeader = (props) => {
  const {
    globalStoreData,
    rules,
    appName,
    isLoading,
    className,
    isHeaderLogoVisible,
    PERMISSIONS_CONSTANTS,
    permissions,
    children,
    logoConfig,
    changeUserOptions,
    hideAdminToolsDropdownLink,
    ...other
  } = props;
  
  const check = (rule) => checkBase(globalStoreData, rule);
  const data = {
    ...globalStoreData,
    ...other,
  };
  const widgets = {
    ...WIDGETS,
    notification: data.notificationConfig,
    voice: data.voiceConfig,
    ...data.widgets,
  };

  // remove Admin widget from Admin apps
  if (appName === 'Administration Tools') {
    widgets.admin = null;
  }

  const userConfig = {
    name: getInitials(data.firstName, data.lastName) || getInitials(get(data.user, 'profileDTO.displayName')),
    options: changeUserOptions(
      userDropdownOptions
        .filter(
          (o) =>
            !(hideAdminToolsDropdownLink && (o.label === 'Administration' || o.label === 'Administration Tools')),
        )
        .filter((o) => (o.rules ? o.rules.some(check) : true))
        // if we have admin widget, remove admin links from user dropdown
        .filter((o) => (widgets.admin ? !o.isAdminTool : true)),
    ).map((o) => pick(o, ['label', 'href', 'onClick'])),
  };

  const permissionProps = {
    permissions,
    ...globalStoreData,
    rules,
    PERMISSIONS_CONSTANTS,
  };

  const fallbackKey = 'nextOS';
  let appKey;

  if (appName) {
    // detect app settings by title passed via appName prop
    appKey = Object.keys(appList).find((key) => {
      return appList[key].title === appName;
    });
  }

  if (!appKey) {
    // detect app settings by window url
    const url = typeof window === 'undefined' ? '' : window.location.pathname;
    const keys = Object.keys(appList);

    appKey =
      keys.find((key) => {
        const { location } = appList[key];
        // is urls beginning match with location
        const starts = url.indexOf(location) === 0;
        // is urls ending match with location
        const ends = ['', '/', '?', '#'].indexOf(url.slice(location.length)[0] || '') > -1;

        return starts && ends;
      }) || fallbackKey;
  }

  return (
    <header className={cx(blockName, className)}>
      {isHeaderLogoVisible && <HeaderLogo config={logoConfig ? logoConfig : { href: routes.home }} />}
      <div className={`${blockName}__wrapper`}>
        {!isHeaderLogoVisible && (
          <div className="nx-global-header-app">
            <img className="nx-global-header-app__icon" src={appIcons[appKey] || appIcons[fallbackKey]} />
            <div className="nx-global-header-app__name">{appName || appList[appKey].title}</div>
          </div>
        )}
        {children && <div className={`${blockName}__content`}>{children}</div>}
        <HeaderButtons
          isLoading={isLoading}
          permissionProps={permissionProps}
          widgets={widgets}
          userConfig={userConfig}
        />
      </div>
    </header>
  );
};

SimpleHeader.propTypes = {
  appName: PropTypes.string,
  className: PropTypes.string,
  globalStoreData: PropTypes.object,
  isLoading: PropTypes.bool,
  rules: PropTypes.array,
  isHeaderLogoVisible: PropTypes.bool,
  hideAdminToolsDropdownLink: PropTypes.bool,
  PERMISSIONS_CONSTANTS: PropTypes.array,
  permissions: PropTypes.array,
  changeUserOptions: PropTypes.func,
  logoConfig: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
  }),
  children: PropTypes.any,
};

SimpleHeader.defaultProps = {
  appName: '',
  globalStoreData: {},
  isLoading: false,
  rules: [],
  dropdownOptions: [],
  isHeaderLogoVisible: false,
  hideAdminToolsDropdownLink: false,
  changeUserOptions: identity,
};